"use client"

import HomeDetailsWrapperAccordion from "../home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import { workScopeStatusOptions } from "../../constants/options"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.electricalInfo"


function HomeElectricalForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillElectricalOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillElectricalOn` })
    const goodwillElectricalRoughInOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillElectricalRoughInOn` })
    
    // Watch status fields and claimed by fields
    const electrical = useWatch({ control, name: `${BASE_FIELD_NAME}.electrical` })
    const electricalClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const electricalRoughIn = useWatch({ control, name: `${BASE_FIELD_NAME}.electricalRoughIn` })
    const electricalRoughInClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.electricalRoughInClaimedBy` })
    const goodwillElectrical = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillElectrical` })
    const goodwillElectricalClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillElectricalClaimedBy` })
    const goodwillElectricalRoughIn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillElectricalRoughIn` })
    const goodwillElectricalRoughInClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillElectricalRoughInClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: electrical,
        claimedBy: electricalClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: electricalRoughIn,
        claimedBy: electricalRoughInClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.electricalRoughInClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillElectrical,
        claimedBy: goodwillElectricalClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillElectricalClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillElectricalOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillElectricalRoughIn,
        claimedBy: goodwillElectricalRoughInClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillElectricalRoughInClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillElectricalRoughInOn,
    })

    return (
        <HomeDetailsWrapperAccordion 
            heading="Electrical"
            wrapperClassName="xl:grid-cols-2"
        >
            <WorkScopeStatusSelect workScopeSlug="electrical-fixtures"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Electrical" toggleName={`${BASE_FIELD_NAME}.goodwillElectricalOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.electrical`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillElectricalOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Electrical Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillElectrical`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Electrical Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillElectricalClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <WorkScopeStatusSelect workScopeSlug="rough-electrical"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Electrical Rough In" toggleName={`${BASE_FIELD_NAME}.goodwillElectricalRoughInOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.electricalRoughIn`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Electrical Rough In Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.electricalRoughInClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillElectricalRoughInOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Electrical Rough In Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillElectricalRoughIn`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Electrical Rough In Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillElectricalRoughInClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Give a brief description of the electrical work needed",
                    wrapperClassName: "xl:col-span-2"
                }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeElectricalForm
